import { CurrencyPipe, DatePipe } from "@angular/common";
import { Pipe, PipeTransform } from "@angular/core";
import { PaymentMethod } from "@app/modules/create-order/entities/create-order.entities";
import { BoxPendingOrder } from "@app/modules/orders/entities/orders.entities";
import { TransportMediaType } from "@core/entities";
import { timezoneOffset } from "@core/utils";
import { environment as env, environment } from "@environments/environment";

export enum CancellationMoment {
  RT_UNASSIGNED = "RT_UNASSIGNED",
  RT_TO_STORE = "RT_TO_STORE",
  RT_IN_STORE = "RT_IN_STORE",
  RT_TO_USER = "RT_TO_USER",
  RT_AT_USER = "RT_AT_USER",
}

@Pipe({ name: "parsePaymentMethod" })
export class ParsePaymentMethodPipe implements PipeTransform {
  transform(b: PaymentMethod | undefined, collect_cash?: number): string {
    switch (b) {
      case PaymentMethod.CASH:
        return "SHARED.PAYMENT_METHOD.CASH";
      case PaymentMethod.POS_TERMINAL:
        return "SHARED.PAYMENT_METHOD.POS_TERMINAL";
      case PaymentMethod.ONLINE:
        return "SHARED.PAYMENT_METHOD.ONLINE";
      default:
        switch (collect_cash) {
          case 1:
            return "SHARED.PAYMENT_METHOD.CASH";
          case 0:
            return "SHARED.PAYMENT_METHOD.ONLINE";
          default:
            break;
        }
        break;
    }
  }
}

@Pipe({ name: "parsePaymentMethodIcon" })
export class ParsePaymentMethodIconPipe implements PipeTransform {
  transform(b: PaymentMethod, collect_cash?: number): string {
    switch (b) {
      case PaymentMethod.CASH:
        return "assets/icons/svg/coin.svg";
      case PaymentMethod.POS_TERMINAL:
        return "assets/icons/svg/smartphone.svg";
      case PaymentMethod.ONLINE:
        return "assets/icons/svg/globe.svg";
      default:
        switch (collect_cash) {
          case 1:
            return "assets/icons/svg/coin.svg";
          case 0:
            return "assets/icons/svg/globe.svg";
          default:
            break;
        }
        break;
    }
  }
}

const carIcon = "assets/icons/svg/car.svg";
const bikeIcon = "assets/icons/svg/bike.svg";

@Pipe({ name: "parseVehicle" })
export class ParseVehiclePipe implements PipeTransform {
  transform(needsCar: boolean): string {
    if (needsCar) {
      return carIcon;
    }
    return bikeIcon;
  }
}

@Pipe({ name: "parseCancelationReason" })
export class ParseCancelationReason implements PipeTransform {
  transform(cr: string | null): string {
    if (!cr) return "-";
    return "ACTIVE_ORDERS.ORDER_DETAIL.CANCELATION_REASONS." + cr;
  }
}

@Pipe({ name: "parseCancelationMoment" })
export class ParseCancelationMoment implements PipeTransform {
  transform(cm: CancellationMoment | null): string {
    if (!cm) return "-";
    return `SETTLEMENT.SETTLEMENT_TABLE.CANCELATION_MOMENT.${cm}`;
  }
}

@Pipe({ name: "parseDistance" })
export class ParseDistance implements PipeTransform {
  transform(cm: number, units: string): string {
    return [cm.toFixed(2), units].join(" ");
  }
}

@Pipe({ name: "cargoCurrency" })
export class ParseCurrency implements PipeTransform {
  transform(t: number | string): string {
    const cpipe = new CurrencyPipe("en-US");
    return `${env.currencySymbol} ${cpipe.transform(t, env.currency, "", "1.0-" + env.decimalNumber)}`;
  }
}

@Pipe({ name: "parseMinutes" })
export class ParseTimePipe implements PipeTransform {
  transform(t: number): string {
    return Math.round(t) + " mins";
  }
}

@Pipe({ name: "cargoDate" })
export class ParseDate implements PipeTransform {
  transform(t: string | Date, format: string = "shortTime", removeTimeBeforeParsing: boolean = false): string {
    const dpipe = new DatePipe(environment.country.defaultLocale);
    let value: string | Date = t;
    if (removeTimeBeforeParsing && t) {
      const iso = t instanceof Date ? t.toISOString() : t;
      const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
      if (match) {
        const [, year, month, day] = match;
        value = new Date(Number(year), Number(month) - 1, Number(day));
      }
      // A date-only value is built as local midnight on purpose; shifting it to the environment zone could move the day.
      return dpipe.transform(value, format);
    }
    // Render the instant in the environment's zone (e.g. Brasilia for BR) instead of the browser's local zone.
    const instant = value instanceof Date ? value : new Date(value);
    return dpipe.transform(value, format, timezoneOffset(instant, environment.timezone));
  }
}

@Pipe({ name: "boxDate" })
export class ParseBoxDate implements PipeTransform {
  transform(t: BoxPendingOrder["created_at"], format: string = "shortTime"): string {
    const dpipe = new DatePipe(environment.country.defaultLocale, t.timezone);
    const x = dpipe.transform(t.date, format);
    return x;
  }
}

@Pipe({
  name: "parseTransportType",
})
export class ParseTransportTypePipe implements PipeTransform {
  transform(type: TransportMediaType): string {
    switch (type) {
      case "motorbike":
      case "bicycle":
        return "TWO_WHEELS";
      case "car":
        return "CAR";
      default:
        return "NEITHER";
    }
  }
}
